import { useState, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import type { GccS0bTaskEntry, GccS0bReview } from '@shared/types'
import {
  GCC_AUTOMATE_FIRST_LABEL,
  dispositionMeetsRules, indexTaskTagsByName, resolveEligibilityRules, resolveRowDisposition, resolveTaskEligibility,
  type GccRowDisposition, type GccTaskEligibility,
} from '@tn/shared'
import { useApi } from '../index'
import { fmtFte } from './GccResidualWorkload'

const PAGE_SIZE = 25

/** Per-row view of the selected rules. Kept off GccS0bTaskEntry so it isn't persisted with the review. */
interface RowRules extends GccRowDisposition {
  eligibility: GccTaskEligibility
  /** The row's disposition passes the disposition rules selected in GCC Config */
  dispositionSelected: boolean
  /** Step 2 rationale when the task is automate first and stays Retained (WI 2048) */
  automateFirstRationale?: string
}

/** Step 2 task sourcing is keyed by function, process (its capability) and task name. */
const sourcingKey = (functionScope: string, processName: string, taskName: string) =>
  JSON.stringify([functionScope, processName, taskName])

/** Bucket badge is greyed out when the row's disposition or tier rules it out (WI 2366). */
function isBucketMuted(row: RowRules): boolean {
  return row.eligibility === 'Retained' || !row.dispositionSelected
}

function bucketTitle(row: RowRules): string | undefined {
  if (row.disposition === undefined) return 'Step 1 has not tagged this task with a disposition'
  const reasons: string[] = []
  if (!row.dispositionSelected) {
    reasons.push(`${row.disposition} is not among the dispositions selected in GCC Config`)
  } else if (row.eligibility === 'Retained' && row.sourcingTier) {
    reasons.push(`${row.disposition} is selected, but the ${row.sourcingTier} sourcing tier is excluded in GCC Config`)
  }
  if (row.overriddenBucket) {
    reasons.push(`Step 1 tagging changed the Step 0 bucket from ${row.overriddenBucket} to ${row.disposition}`)
  }
  return reasons.length > 0 ? reasons.join('. ') : undefined
}

const ELIGIBILITY_BADGE: Record<GccTaskEligibility, string> = {
  Eligible: 'bg-emerald-50 text-emerald-700',
  Retained: 'bg-gray-100 text-gray-600',
  Unknown: 'bg-amber-50 text-amber-700',
}

const ELIGIBILITY_TITLE: Record<GccTaskEligibility, string> = {
  Eligible: 'Meets all eligibility rules selected in GCC Config',
  Retained: 'Does not meet every eligibility rule selected in GCC Config',
  Unknown: 'No disposition tag found for this task, so its sourcing tier is unknown',
}

interface Props {
  projectId: string
  onConfirm: (review: GccS0bReview) => void
  onSkip: () => void
}

export default function GccTaskRegisterReview({ projectId, onConfirm, onSkip }: Props) {
  const [tasks, setTasks] = useState<GccS0bTaskEntry[]>([])
  const [loading, setLoading] = useState(true)
  // Keyed by row id
  const [rowRules, setRowRules] = useState<Record<string, RowRules>>({})
  // Opens on the tasks that meet the selected rules; All keeps the Retained tasks reviewable (WI 2366)
  const [filter, setFilter] = useState<'all' | 'flagged' | 'eligible'>('eligible')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const api = useApi()

  useEffect(() => {
    void api.getProject(projectId).then(project => {
      const register = project?.gccData?.taskRegister?.taskRegister
      if (!register) { setLoading(false); return }
      const entries: GccS0bTaskEntry[] = register.flatMap(proc =>
        proc.tasks.map(task => ({
          id: uuidv4(),
          processName: proc.processName,
          functionScope: proc.functionScope,
          taskName: task.name,
          roleTitle: task.roleTitle,
          timePct: task.timePct,
          // v2 rows carry FTE and no bucket (WI 2323); undefined fields are left off
          ...(task.fte !== undefined && { fte: task.fte }),
          ...(task.automationBucket !== undefined && { automationBucket: task.automationBucket }),
          source: (task.source ?? 'LLM') as GccS0bTaskEntry['source'],
          flagged: false,
          excluded: false,
        }))
      )
      // Eligibility follows the rules selected in GCC Config (WI 2366)
      const rules = resolveEligibilityRules(project?.gccData?.eligibilityRules)
      const tagsByName = indexTaskTagsByName(project?.gccData?.taggedTasks?.taggedTasks)
      const automateFirst = new Map(
        (project?.gccData?.sourcingModel?.taskSourcing ?? [])
          .filter(s => s.automateFirst)
          .map(s => [sourcingKey(s.functionScope, s.capabilityName, s.taskName), s.rationale])
      )
      setRowRules(Object.fromEntries(entries.map(e => {
        const row = resolveRowDisposition(e, tagsByName)
        return [e.id, {
          ...row,
          eligibility: resolveTaskEligibility(e, tagsByName, rules),
          dispositionSelected: dispositionMeetsRules(row.disposition, rules),
          automateFirstRationale: automateFirst.get(sourcingKey(e.functionScope, e.processName, e.taskName)),
        }]
      })))
      setTasks(entries)
      setLoading(false)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api is stable from context
  }, [projectId])

  const updateTask = (id: string, patch: Partial<GccS0bTaskEntry>) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t))
  }

  const flaggedCount = tasks.filter(t => t.flagged).length
  const excludedCount = tasks.filter(t => t.excluded).length
  const isEligible = (t: GccS0bTaskEntry) => rowRules[t.id]?.eligibility === 'Eligible'
  const eligibleCount = tasks.filter(isEligible).length
  // v2 baselines carry task FTE and no Step 0 bucket, so the column shows the Step 1 disposition (WI 2323)
  const hasFte = tasks.some(t => t.fte !== undefined)

  const q = search.trim().toLowerCase()
  const flagFilteredTasks =
    filter === 'flagged' ? tasks.filter(t => t.flagged) :
    filter === 'eligible' ? tasks.filter(isEligible) :
    tasks
  const searchedTasks = q
    ? flagFilteredTasks.filter(t =>
        t.taskName.toLowerCase().includes(q) ||
        t.processName.toLowerCase().includes(q) ||
        t.roleTitle.toLowerCase().includes(q) ||
        (rowRules[t.id]?.disposition ?? t.automationBucket ?? '').toLowerCase().includes(q)
      )
    : flagFilteredTasks
  const totalPages = Math.max(1, Math.ceil(searchedTasks.length / PAGE_SIZE))
  const pagedTasks = searchedTasks.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-8 py-16 text-center">
        <div className="text-4xl mb-3">📋</div>
        <p className="text-sm text-gray-500 animate-pulse">Loading task register…</p>
      </div>
    )
  }

  if (tasks.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-8 py-16 text-center">
        <div className="text-4xl mb-3">⚠️</div>
        <p className="text-sm text-gray-500 mb-4">Task register not yet available. The pipeline may still be processing Step 0.</p>
        <button onClick={onSkip} className="text-sm text-purple-600 hover:text-purple-700 font-medium">
          Continue without review →
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto px-8 py-8">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-1">Review Task Register</h2>
        <p className="text-sm text-gray-500 max-w-2xl">
          Verify the AI-generated task register before the 5-tier sourcing analysis runs.
          Flag tasks that need attention or exclude incorrect tasks from the analysis.
        </p>
        {(flaggedCount > 0 || excludedCount > 0) && (
          <div className="mt-3 flex gap-3">
            {flaggedCount > 0 && (
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                {flaggedCount} flagged
              </span>
            )}
            {excludedCount > 0 && (
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-red-50 text-red-600 border border-red-200">
                {excludedCount} excluded
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 mb-4">
        <input
          type="search"
          placeholder="Search tasks, processes, roles…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0) }}
          className="flex-1 max-w-xs text-xs px-3 py-1.5 rounded-lg border border-gray-200 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300"
          aria-label="Search task register"
        />
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg ml-auto">
          <button
            onClick={() => { setFilter('eligible'); setPage(0) }}
            title="Tasks that meet every eligibility rule selected in GCC Config"
            aria-pressed={filter === 'eligible'}
            className={`px-3 py-1 text-xs rounded-md font-medium transition-all ${filter === 'eligible' ? 'bg-white shadow-sm text-emerald-700' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Meets selected rules ({eligibleCount})
          </button>
          <button
            onClick={() => { setFilter('all'); setPage(0) }}
            aria-pressed={filter === 'all'}
            className={`px-3 py-1 text-xs rounded-md font-medium transition-all ${filter === 'all' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
          >
            All ({tasks.length})
          </button>
          <button
            onClick={() => { setFilter('flagged'); setPage(0) }}
            aria-pressed={filter === 'flagged'}
            className={`px-3 py-1 text-xs rounded-md font-medium transition-all ${filter === 'flagged' ? 'bg-white shadow-sm text-amber-700' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Flagged ({flaggedCount})
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Process</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Task</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Role</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Time %</th>
                {hasFte && <th className="text-right px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">FTE</th>}
                <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">{hasFte ? 'Disposition' : 'Bucket'}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">GCC Eligibility</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Flag</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Exclude</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pagedTasks.map(task => {
                const row = rowRules[task.id]
                return (
                  <tr
                    key={task.id}
                    className={`transition-colors ${
                      task.excluded ? 'opacity-40 bg-gray-50 line-through' :
                      task.flagged ? 'bg-amber-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-4 py-2.5 text-gray-600 min-w-[140px] max-w-[180px] break-words">{task.processName}</td>
                    <td className="px-4 py-2.5 text-gray-900 font-medium max-w-[200px]">{task.taskName}</td>
                    <td className="px-4 py-2.5 text-gray-600 min-w-[140px] max-w-[180px] break-words">{task.roleTitle}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600">{task.timePct}%</td>
                    {hasFte && <td className="px-4 py-2.5 text-right text-gray-600">{task.fte !== undefined ? fmtFte(task.fte) : '—'}</td>}
                    <td className="px-4 py-2.5">
                      {/* Shows the disposition the rules were checked against, so Bucket and Eligibility agree */}
                      <span
                        title={row && bucketTitle(row)}
                        data-muted={row && isBucketMuted(row) ? 'true' : undefined}
                        className={`px-2 py-0.5 rounded-full font-medium ${
                          row && isBucketMuted(row) ? 'bg-gray-100 text-gray-400' : 'bg-purple-50 text-purple-700'
                        }`}
                      >
                        {row?.disposition ?? task.automationBucket ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      {row && (
                        <span
                          title={ELIGIBILITY_TITLE[row.eligibility]}
                          className={`px-2 py-0.5 rounded-full font-medium ${ELIGIBILITY_BADGE[row.eligibility]}`}
                        >
                          {row.eligibility}
                        </span>
                      )}
                      {row?.automateFirstRationale && (
                        <span
                          title={row.automateFirstRationale}
                          className="ml-1 px-2 py-0.5 rounded-full font-medium bg-teal-50 text-teal-700 whitespace-nowrap"
                        >
                          {GCC_AUTOMATE_FIRST_LABEL}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <button
                        onClick={() => updateTask(task.id, { flagged: !task.flagged })}
                        title={task.flagged ? 'Remove flag' : 'Flag for review'}
                        className={`text-base leading-none transition-colors ${task.flagged ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}
                      >
                        ⚑
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <input
                        type="checkbox"
                        checked={task.excluded}
                        onChange={e => updateTask(task.id, { excluded: e.target.checked })}
                        className="w-3.5 h-3.5 accent-purple-600 cursor-pointer"
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {searchedTasks.length === 0 && (
            <div className="py-8 text-center text-sm text-gray-400">
              {q ? `No tasks match "${search}".` :
                filter === 'eligible' ? 'No tasks meet the selected eligibility rules. Select All to see every task.' :
                'No flagged tasks.'}
            </div>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={page === 0}
              className="px-2.5 py-1 rounded-md border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              aria-label="Previous page"
            >
              ←
            </button>
            <span>Page {page + 1} of {totalPages}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages - 1}
              className="px-2.5 py-1 rounded-md border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              aria-label="Next page"
            >
              →
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={onSkip}
          className="text-sm text-gray-500 hover:text-gray-700 font-medium px-4 py-2.5 rounded-lg hover:bg-gray-50 transition-all"
        >
          Skip review →
        </button>
        <button
          onClick={() => onConfirm({ tasks, proceededAt: new Date().toISOString() })}
          className="px-6 py-2.5 rounded-lg text-white font-semibold text-sm transition-all hover:opacity-90"
          style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
        >
          Confirm &amp; Proceed
        </button>
      </div>
    </div>
  )
}
